// Native camera + photo library wrapper.
// Returns a File object (web) or a File built from a base64 data URL (native)
// so existing upload flows that expect File continue to work unchanged.
//
// On native iOS/Android the system camera UI is presented (full-screen, native
// shutter, retake/use flow). On web we fall back to <input type="file" capture>.

import { Capacitor } from "@capacitor/core";

export type PhotoSource = "camera" | "library";

interface PickPhotoOptions {
  source?: PhotoSource;
  quality?: number; // 0-100, default 85
  allowEditing?: boolean;
  fileNameHint?: string;
}

function dataUrlToFile(dataUrl: string, fileName: string, mime: string): File {
  const arr = dataUrl.split(",");
  const bstr = atob(arr[1] ?? "");
  const u8 = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
  return new File([u8], fileName, { type: mime });
}

/**
 * Capture a photo using the native camera (iOS/Android) or the photo library.
 * Resolves to a File ready for upload, or null if the user cancels.
 */
export async function pickPhoto(opts: PickPhotoOptions = {}): Promise<File | null> {
  const { source = "camera", quality = 85, allowEditing = false } = opts;
  if (!Capacitor.isNativePlatform()) return null;

  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const result = await Camera.getPhoto({
      quality,
      allowEditing,
      resultType: CameraResultType.DataUrl,
      source: source === "library" ? CameraSource.Photos : CameraSource.Camera,
      saveToGallery: false,
      correctOrientation: true,
    });
    const dataUrl = result.dataUrl;
    if (!dataUrl) return null;
    const fmt = result.format || "jpeg";
    const mime = `image/${fmt === "jpg" ? "jpeg" : fmt}`;
    const name = `${opts.fileNameHint ?? "photo"}-${Date.now()}.${fmt}`;
    return dataUrlToFile(dataUrl, name, mime);
  } catch (err) {
    // user-cancel surfaces as a thrown error in @capacitor/camera
    console.info("[native-camera] pickPhoto cancelled or failed", err);
    return null;
  }
}

/** Best-effort permission probe; returns true if camera is granted or prompt is fine to surface. */
export async function ensureCameraPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return true;
  try {
    const { Camera } = await import("@capacitor/camera");
    const status = await Camera.checkPermissions();
    if (status.camera === "granted") return true;
    const req = await Camera.requestPermissions({ permissions: ["camera", "photos"] });
    return req.camera === "granted";
  } catch (err) {
    console.warn("[native-camera] permission probe failed", err);
    return false;
  }
}
